import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Pencil, UserPlus } from "lucide-react";
import { opsSupplierApi, type OpsSupplier } from "@/api/opsSupplierClient";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/Pagination";
import { useTranslation } from "@/framework-originated/i18n";

// /suppliers — 运营端供应商账号管理(doc/049 P5):创建(初始密码 bcrypt 落库,
// D4)、软停用(D1)、目标分配入口。与供应商端 /supplier/* 语义区分。
const emptyForm = {
  username: "",
  password: "",
  contact_name: "",
  contact_email: "",
  distribute_limit: 0,
  code_prefix: "",
  enabled: true,
};

export function SuppliersPage() {
  const qc = useQueryClient();
  const { t } = useTranslation("opsSuppliers");
  const [q, setQ] = useState("");
  const [enabled, setEnabled] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-suppliers", q, enabled, page, pageSize],
    queryFn: () =>
      opsSupplierApi.list({
        q: q || undefined,
        enabled: enabled === "" ? undefined : enabled === "enabled",
        page,
        page_size: pageSize,
      }),
  });

  const createMut = useMutation({
    mutationFn: () =>
      opsSupplierApi.create({
        username: form.username.trim(),
        password: form.password,
        contact_name: form.contact_name.trim() || undefined,
        contact_email: form.contact_email.trim() || undefined,
        distribute_limit: form.distribute_limit > 0 ? form.distribute_limit : undefined,
        code_prefix: form.code_prefix.trim() || undefined,
        enabled: form.enabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops-suppliers"] });
      setCreating(false);
      setForm(emptyForm);
      setErr(null);
      setMsg(t("created"));
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const disableMut = useMutation({
    mutationFn: (id: number) => opsSupplierApi.disable(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops-suppliers"] });
      setMsg(t("disabled"));
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "停用失败"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t("title")}</h1>
        <Button onClick={() => { setCreating(true); setMsg(null); setErr(null); }}>
          <UserPlus className="mr-1 h-4 w-4" /> {t("create")}
        </Button>
      </div>

      {msg && <div className="text-sm text-muted-foreground">{msg}</div>}

      {creating && (
        <Card>
          <CardContent className="pt-6">
            {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
            <form
              className="grid grid-cols-2 gap-3"
              onSubmit={(e) => { e.preventDefault(); setErr(null); createMut.mutate(); }}
            >
              <Field label={t("username")}>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required maxLength={50} />
              </Field>
              <Field label={t("password")} hint={t("passwordHint")}>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} maxLength={128} />
              </Field>
              <Field label={t("contactName")}>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} maxLength={100} />
              </Field>
              <Field label={t("contactEmail")}>
                <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} maxLength={255} />
              </Field>
              <Field label={t("distributeLimit")} hint={t("distributeLimitHint")}>
                <Input type="number" min="0" value={form.distribute_limit} onChange={(e) => setForm({ ...form, distribute_limit: Number(e.target.value) })} />
              </Field>
              <Field label={t("codePrefix")} hint={t("codePrefixHint")}>
                <Input value={form.code_prefix} onChange={(e) => setForm({ ...form, code_prefix: e.target.value })} maxLength={8} pattern="[A-Za-z0-9]{0,8}" placeholder="S" />
              </Field>
              <Field label={t("enabled")}>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-4 w-4 rounded border" />
                  {form.enabled ? t("statusEnabled") : t("statusDisabled")}
                </label>
              </Field>
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => { setCreating(false); setErr(null); }}>{t("cancel")}</Button>
                <Button type="submit" disabled={createMut.isPending}>
                  {createMut.isPending ? "…" : t("save")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2">
        <Input placeholder={t("searchPlaceholder")} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="max-w-xs" />
        <select className="h-10 rounded-md border bg-background px-3 text-sm" value={enabled} onChange={(e) => { setEnabled(e.target.value); setPage(1); }}>
          <option value="">{t("allStatus")}</option>
          <option value="enabled">{t("statusEnabled")}</option>
          <option value="disabled">{t("statusDisabled")}</option>
        </select>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading && <div className="text-sm text-muted-foreground">…</div>}
          {error && <div className="text-sm text-destructive">{String(error)}</div>}
          {data && data.items.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">{t("empty")}</div>
          )}
          {data && data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="py-2">ID</th>
                  <th>{t("username")}</th>
                  <th>{t("contactName")}</th>
                  <th>{t("contactEmail")}</th>
                  <th>{t("codePrefix")}</th>
                  <th>{t("statusEnabled")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((s: OpsSupplier) => (
                  <tr key={s.id} className="border-b hover:bg-muted/30">
                    <td className="py-2">{s.id}</td>
                    <td className="font-medium">{s.username}</td>
                    <td>{s.contact_name || "—"}</td>
                    <td className="text-xs text-muted-foreground">{s.contact_email || "—"}</td>
                    <td className="font-mono text-xs">{s.code_prefix}</td>
                    <td>{s.enabled ? <Badge variant="success">{t("statusEnabled")}</Badge> : <Badge variant="muted">{t("statusDisabled")}</Badge>}</td>
                    <td className="text-right space-x-1">
                      <Link
                        to="/suppliers/$id"
                        params={{ id: String(s.id) }}
                        className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs hover:bg-accent"
                        title={t("detail.title")}
                      >
                        <Pencil className="h-3.5 w-3.5" /> {t("edit")}
                      </Link>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={disableMut.isPending || !s.enabled}
                        onClick={() => {
                          if (confirm(t("disableConfirm", { name: s.username }))) disableMut.mutate(s.id);
                        }}
                      >
                        {t("disable")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            className="mt-3"
            page={page}
            total={data?.total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children, className = "", hint }: { label: string; children: React.ReactNode; className?: string; hint?: string }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground leading-relaxed">{hint}</p>}
    </div>
  );
}
